import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { ThemeProvider } from '@mui/material';
import dayjs from 'dayjs';

import {
	useDTicketSubscriptionOffering,
	useNotificationPermission,
	usePassengerPushes,
	usePaymentMethod,
	useSubscription,
	useSubscriptionActions,
	useSubscriptionTicketActions,
	useUser,
	useUserActions,
} from '@mopla/business-logic';
import { passengerThemeV2 } from '@mopla/mui';
import {
	PaymentPortal,
	usePaymentPortalControls,
	useRestoreState,
} from '@mopla/payment-flow';
import {
	BlackOverlay,
	ErrorAnim,
	MplButton,
	MplContentLoader,
	MplModal,
	PermissionsModal,
	RecaptchaHandle,
	StayInformedAnim,
} from '@mopla/ui';
import { isAfterTenthOfMonth } from '@mopla/utils';

import passApi from '../../../api/passApi';
import {
	pushPaymentMethodAddedEvent,
	pushPurchaseEvent,
	pushSelectSubscriptionMonthEvent,
} from '../../../app/userTracking/events';
import { usePassengerAppNavigate } from '../../../hooks';

import { Step1Content } from './Step1Content';
import { Step1JobticketContent } from './Step1JobticketContent';
import {
	IStep2FormValues,
	Step2SelectPaymentMethod,
} from './Step2SelectPaymentMethod';
import { Step3SummaryContent } from './Step3SummaryContent';
import { SubscribeDTicketBottomSheet } from './SubscribeDTicketBottomSheet';
import { SubscribeDTicketLayout } from './SubscribeDTicketLayout';
import { SubscribeDTicketRecaptcha } from './SubscribeDTicketRecaptcha';
import { SuccessStep } from './SuccessStep';

enum ESubscribeStep {
	SubscriptionInfo = 'subscriptionInfo',
	PaymentMethod = 'payment',
	Summary = 'summary',
	Success = 'success',
}

interface IRestoreState {
	activeStep: ESubscribeStep;
	monthOfStart: string;
	totalSteps: number;
}

/** The fixed step sequences for each flow variant */
const STEPS_WITH_PAYMENT: ESubscribeStep[] = [
	ESubscribeStep.SubscriptionInfo,
	ESubscribeStep.PaymentMethod,
	ESubscribeStep.Summary,
];
const STEPS_WITHOUT_PAYMENT: ESubscribeStep[] = [
	ESubscribeStep.SubscriptionInfo,
	ESubscribeStep.Summary,
];

export const SubscribeDTicket = () => {
	const { t } = useTranslation([
		'subscribeDTicket',
		'dticket',
		'errors',
		'booking',
		'permissions',
	]);
	const [query] = useSearchParams();
	const { navigateToHome, navigateToUserDTicket } = usePassengerAppNavigate();

	const [activeStep, setActiveStep] = useState<ESubscribeStep>(
		ESubscribeStep.SubscriptionInfo
	);
	const [error, setError] = useState(false);
	const [isSubscribing, setIsSubscribing] = useState(false);
	const [isAcceptTariffs, setIsAcceptTariffs] = useState(false);
	const [isAcceptEarlyServiceStart, setIsAcceptEarlyServiceStart] =
		useState(false);
	/** Selected month for jobticket users who can choose from multiple offers */
	const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
	/** Whether we're waiting for payment method to be confirmed after adding */
	const [isWaitingForPayment, setIsWaitingForPayment] = useState(false);
	/** Lock the total steps at flow initialization so it doesn't change mid-flow */
	const [lockedTotalSteps, setLockedTotalSteps] = useState<number | null>(null);
	const recaptchaRef = useRef<RecaptchaHandle>(null);
	/** Track if payment portal was ever opened to avoid false triggers on mount */
	const wasPaymentPortalOpenedRef = useRef(false);

	const paymentFormMethods = useForm<IStep2FormValues>({
		defaultValues: { paymentMethod: undefined },
	});

	const { subscriptionData: subscription } = useSubscription();
	const { fetchSubscriptions } = useSubscriptionActions();
	const { fetchTickets } = useSubscriptionTicketActions();
	const { userData } = useUser();
	const { fetchUser } = useUserActions();
	const { sendMessagingToken } = usePassengerPushes();
	const {
		showPermissionModal,
		checkAndRequestPermission,
		closePermissionModal,
	} = useNotificationPermission();
	const {
		openPaymentPortal,
		closePaymentPortal,
		isPaymentPortalOpened,
		setPaymentStep,
	} = usePaymentPortalControls();
	const { defaultPaymentMethod, refetchPaymentMethod, isPaymentMethodSaving } =
		usePaymentMethod();

	// Reset payment portal state on unmount so it doesn't persist across navigations
	useEffect(() => {
		return () => {
			closePaymentPortal();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Pass selectedMonth so subscriptionOfferByMonth updates for jobticket selection
	const { subscriptionOffering, subscriptionOfferByMonth } =
		useDTicketSubscriptionOffering(selectedMonth ?? undefined);
	const isJobticket = !!subscriptionOffering?.isJobticket;
	const isPriceZero = subscriptionOffering?.price === 0;

	// For jobticket: use the selected month; for regular: use first available offer
	const startDate = useMemo(() => {
		if (isJobticket) return selectedMonth ?? '';
		if (!subscriptionOfferByMonth?.startDate) return '';
		return subscriptionOfferByMonth.startDate;
	}, [isJobticket, selectedMonth, subscriptionOfferByMonth]);

	// Redirect if subscription already exists
	useEffect(() => {
		if (
			subscription?.id &&
			!isSubscribing &&
			activeStep === ESubscribeStep.SubscriptionInfo
		) {
			navigateToUserDTicket();
		}
	}, [subscription?.id, isSubscribing, activeStep, navigateToUserDTicket]);

	// Lock totalSteps on first render when user data and subscription offering are available
	useEffect(() => {
		if (lockedTotalSteps !== null) return; // Already locked
		if (!userData) return; // Wait for user data
		if (!subscriptionOffering) return; // Wait for subscription offering (needed for isPriceZero)

		const hasPayment = userData.hasDefaultStripePaymentMethod || isPriceZero;
		setLockedTotalSteps(hasPayment ? 2 : 3);
	}, [userData, isPriceZero, lockedTotalSteps, subscriptionOffering]);

	// The fixed step sequence based on locked total steps
	const steps =
		lockedTotalSteps === 2 ? STEPS_WITHOUT_PAYMENT : STEPS_WITH_PAYMENT;
	const stepsAmount = lockedTotalSteps ?? 3;
	const activeStepIndex =
		activeStep === ESubscribeStep.Success
			? steps.length - 1
			: steps.findIndex((step) => step === activeStep);

	const isSubscriptionInfoStep = activeStep === ESubscribeStep.SubscriptionInfo;
	const isPaymentStep = activeStep === ESubscribeStep.PaymentMethod;
	const isSummaryStep = activeStep === ESubscribeStep.Summary;
	const isSuccessStep = activeStep === ESubscribeStep.Success;

	// Validity warning: show when current month is selected and we're past the 10th
	const showValidityWarning = useMemo(() => {
		if (!startDate) return false;
		return isAfterTenthOfMonth(startDate);
	}, [startDate]);

	// Handle jobticket month selection
	const handleSelectMonth = useCallback((month: string) => {
		setSelectedMonth(month);
	}, []);

	const daysRemaining = useMemo(() => {
		return dayjs().endOf('month').diff(dayjs(), 'day') + 1;
	}, []);

	/** State to persist for restoration after payment redirect */
	const stateToRestore = useMemo<IRestoreState>(
		() => ({
			activeStep,
			monthOfStart: startDate,
			totalSteps: stepsAmount,
		}),
		[activeStep, startDate, stepsAmount]
	);

	/** Handle state restoration after payment redirect */
	const handleStateRestored = useCallback((restoredState: IRestoreState) => {
		try {
			// Restore the locked total steps from persisted state
			if (restoredState.totalSteps) {
				setLockedTotalSteps(restoredState.totalSteps);
			}
			// Restore the chosen start month. For the jobticket/discounted flow
			// startDate is derived from selectedMonth, so without this it would be
			// empty after the external payment redirect and the subscription
			// create call would send an "Invalid Date" startDate.
			if (restoredState.monthOfStart) {
				setSelectedMonth(restoredState.monthOfStart);
			}
			// After external redirect, we land back on step 2 (payment)
			// Show loader while payment method is being confirmed
			if (
				restoredState.activeStep === ESubscribeStep.PaymentMethod ||
				restoredState.activeStep === ESubscribeStep.Summary
			) {
				setActiveStep(ESubscribeStep.PaymentMethod);
				setIsWaitingForPayment(true);
			} else {
				setActiveStep(restoredState.activeStep);
			}
		} catch (error) {
			console.error('[SubscribeDTicket] State restoration failed:', error);
			setActiveStep(ESubscribeStep.SubscriptionInfo);
		}
	}, []);

	useRestoreState({
		restoreState: stateToRestore,
		onStateRestored: handleStateRestored,
	});

	/** Query params for restoring after payment redirect with isBankRedirect flag */
	const queryToRestore = useMemo(() => {
		const newQuery = new URLSearchParams(query);
		newQuery.set('isBankRedirect', '1');
		return newQuery.toString();
	}, [query]);

	const nextStep = useCallback(() => {
		const nextIndex = activeStepIndex + 1;
		const maxIndex = steps.length - 1;
		setActiveStep(nextIndex > maxIndex ? steps[maxIndex] : steps[nextIndex]);
	}, [activeStepIndex, steps]);

	const nextStepHandler = async () => {
		if (isSubscriptionInfoStep) {
			pushSelectSubscriptionMonthEvent(startDate);
			return nextStep();
		}

		if (isPaymentStep) {
			const selectedMethod = paymentFormMethods.getValues('paymentMethod');
			if (!selectedMethod) return;
			// Skip the payment selection modal — go directly to the chosen flow
			// SEPA: redirect to TrueLayer authentication
			// Other: go directly to Stripe card flow
			const step = selectedMethod === 'sepa' ? 'sepaRedirect' : 'stripe';
			setPaymentStep(step);
			openPaymentPortal();
			// Override the step after openPaymentPortal (which defaults to 'selection')
			setPaymentStep(step);
			return;
		}

		// Summary step -> create subscription
		try {
			if (isSubscribing) return;

			// Guard against an invalid/empty start date (e.g. state lost on
			// restore) so we never send "Invalid Date" to the backend.
			if (!dayjs(startDate).isValid()) {
				throw new Error('Start date is invalid');
			}

			setIsSubscribing(true);

			if (!recaptchaRef.current) {
				throw new Error('Recaptcha ref is not available');
			}

			const recaptchaToken = await recaptchaRef.current.executeRecaptcha();

			if (!recaptchaToken) {
				throw new Error('Recaptcha token is missing');
			}

			await passApi.post('/api/passengers/subscription/create', {
				startDate: dayjs(startDate).format('YYYY-MM-01'),
				recaptchaToken,
			});

			fetchSubscriptions();
			fetchTickets();
			fetchUser();
			pushPurchaseEvent({
				value: subscriptionOffering?.price,
				shipping: 0.0,
				currency: 'EUR',
				items: [
					{
						item_name: 'Deutschlandticket',
						item_id: 'mp1122',
						price: subscriptionOffering?.price,
						item_brand: 'not necessary',
						item_category: 'Deutschlandticket',
						item_variant: 'Deutschlandticket',
						quantity: 1,
					},
				],
			});

			return setActiveStep(ESubscribeStep.Success);
		} catch (e) {
			setError(true);
			console.log('Error on creating subscription: ', e);
		} finally {
			setIsSubscribing(false);
		}
	};

	const previousStepHandler = useCallback(() => {
		const prevIndex = activeStepIndex - 1;

		if (prevIndex < 0) {
			return navigateToHome();
		}

		// Reset acceptance states when going back from summary
		if (isSummaryStep) {
			setIsAcceptTariffs(false);
			setIsAcceptEarlyServiceStart(false);
		}

		// Reset waiting state when navigating back
		setIsWaitingForPayment(false);

		return setActiveStep(steps[prevIndex]);
	}, [activeStepIndex, steps, navigateToHome, isSummaryStep]);

	const handleErrorRetry = useCallback(() => {
		setError(false);
	}, []);

	// Check notification permission after successful purchase
	useEffect(() => {
		if (activeStep === ESubscribeStep.Success) {
			checkAndRequestPermission('action');
		}
	}, [activeStep, checkAndRequestPermission]);

	// A new payment method was added -> show loader then proceed to summary
	useEffect(() => {
		if (!isPaymentStep) return;
		if (!isWaitingForPayment) return;
		// Wait until payment method is confirmed (not saving anymore) and exists
		if (isPaymentMethodSaving) return;
		if (!defaultPaymentMethod) return;

		pushPaymentMethodAddedEvent();
		// Small delay so user sees the loader state on step 2
		const timer = setTimeout(() => {
			setIsWaitingForPayment(false);
			setActiveStep(ESubscribeStep.Summary);
		}, 600);

		return () => clearTimeout(timer);
	}, [
		defaultPaymentMethod,
		isPaymentStep,
		isWaitingForPayment,
		isPaymentMethodSaving,
	]);

	// Safety timeout: if waiting for payment but nothing arrives, stop waiting
	// This handles cases like cancelled PayPal/Revolut redirects
	useEffect(() => {
		if (!isPaymentStep || !isWaitingForPayment) return;

		const safetyTimer = setTimeout(() => {
			if (!defaultPaymentMethod) {
				setIsWaitingForPayment(false);
				refetchPaymentMethod();
			}
		}, 8000);

		return () => clearTimeout(safetyTimer);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isPaymentStep, isWaitingForPayment]);

	// Dismiss payment portal without triggering the "waiting for payment" loader.
	// Used when user closes the SEPA info popup before actually starting TrueLayer/Stripe.
	const handleDismissPaymentPortal = useCallback(() => {
		wasPaymentPortalOpenedRef.current = false;
		closePaymentPortal();
	}, [closePaymentPortal]);

	// Track when payment portal opens
	useEffect(() => {
		if (isPaymentPortalOpened) {
			wasPaymentPortalOpenedRef.current = true;
		}
	}, [isPaymentPortalOpened]);

	// When payment portal closes, start waiting for payment method
	useEffect(() => {
		if (
			isPaymentStep &&
			!isPaymentPortalOpened &&
			wasPaymentPortalOpenedRef.current
		) {
			wasPaymentPortalOpenedRef.current = false;
			refetchPaymentMethod();
			setIsWaitingForPayment(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isPaymentPortalOpened]);

	// Fetch payment method details when entering Summary step
	useEffect(() => {
		if (isSummaryStep) {
			refetchPaymentMethod();
		}
	}, [isSummaryStep, refetchPaymentMethod]);

	const getButtonText = (): string => {
		switch (activeStep) {
			case ESubscribeStep.Summary:
				return t('subscribeDTicket:bottom_sheet.step_3_button_next');
			default:
				return t('subscribeDTicket:bottom_sheet.step_1_2_button_next');
		}
	};

	const getIsDisabled = (): boolean => {
		switch (activeStep) {
			case ESubscribeStep.SubscriptionInfo:
				return isJobticket ? selectedMonth === null : !startDate;
			case ESubscribeStep.PaymentMethod:
				return !paymentFormMethods.watch('paymentMethod');
			case ESubscribeStep.Summary:
				return !isAcceptTariffs || !isAcceptEarlyServiceStart || isSubscribing;
			default:
				return false;
		}
	};

	const getHeaderTitle = (): string => {
		switch (activeStep) {
			case ESubscribeStep.SubscriptionInfo:
				return t('subscribeDTicket:header.title');
			case ESubscribeStep.PaymentMethod:
				return t('subscribeDTicket:header.title_payment');
			default:
				return t('dticket:header.summary');
		}
	};

	const renderStepContent = () => {
		switch (activeStep) {
			case ESubscribeStep.SubscriptionInfo:
				return isJobticket ? (
					<Step1JobticketContent
						subscriptionOffering={subscriptionOffering}
						subscriptionType={subscriptionOffering?.subscriptionType}
						selectedMonth={selectedMonth}
						onSelectMonth={handleSelectMonth}
						showValidityWarning={showValidityWarning}
						daysRemaining={daysRemaining}
					/>
				) : (
					<Step1Content
						startDate={startDate}
						showValidityWarning={showValidityWarning}
						daysRemaining={daysRemaining}
					/>
				);
			case ESubscribeStep.PaymentMethod:
				// Show loader when waiting for payment method confirmation after redirect/portal close
				if (isWaitingForPayment) {
					return <MplContentLoader />;
				}
				return (
					<FormProvider {...paymentFormMethods}>
						<Step2SelectPaymentMethod />
					</FormProvider>
				);
			case ESubscribeStep.Summary:
				return isSubscribing ? (
					<MplContentLoader />
				) : (
					<Step3SummaryContent
						startDate={startDate}
						isAcceptTariffs={isAcceptTariffs}
						setIsAcceptTariffs={setIsAcceptTariffs}
						isAcceptEarlyServiceStart={isAcceptEarlyServiceStart}
						setIsAcceptEarlyServiceStart={setIsAcceptEarlyServiceStart}
						isPriceZero={isPriceZero}
					/>
				);
			default:
				return null;
		}
	};

	return (
		<ThemeProvider theme={passengerThemeV2}>
			<BlackOverlay open={error} />
			<MplModal
				open={error}
				testId="SubscribeDTicket-MplErrorModal"
				icon={<ErrorAnim loop />}
				title={t('subscribeDTicket:error_modal.title')}
				buttons={
					<MplButton fullWidth onClick={handleErrorRetry}>
						{t('subscribeDTicket:error_modal.button')}
					</MplButton>
				}
			/>
			{/** TODO: redesign-dticket check restorePage url */}
			{isPaymentPortalOpened && (
				<PaymentPortal
					restorePage="/subscribe-dticket"
					restoreQuery={queryToRestore}
					onDismiss={handleDismissPaymentPortal}
				/>
			)}

			<SubscribeDTicketLayout
				title={getHeaderTitle()}
				subtitle={t('subscribeDTicket:header.step_of', {
					step: activeStepIndex + 1,
					total: stepsAmount,
				})}
				currentStep={activeStepIndex + 1}
				totalSteps={stepsAmount}
				onBack={previousStepHandler}
				dataTestId="subscribe-dticket-page"
			>
				{renderStepContent()}
			</SubscribeDTicketLayout>

			<BlackOverlay open={isSuccessStep} />
			<SuccessStep
				open={isSuccessStep}
				startDate={startDate}
				onConfirm={navigateToUserDTicket}
			/>

			{!isSuccessStep && !isWaitingForPayment && !isSubscribing && (
				<SubscribeDTicketBottomSheet
					priceToPay={subscriptionOfferByMonth?.price}
					fullPrice={subscriptionOfferByMonth?.fullPrice}
					buttonLabel={getButtonText()}
					onButtonClick={nextStepHandler}
					isDisabled={getIsDisabled()}
					isLoading={isSubscribing}
				/>
			)}

			{!isPaymentStep && (
				<SubscribeDTicketRecaptcha recaptchaRef={recaptchaRef} />
			)}
			{/** NOTE: DONE - permissions modal */}
			<PermissionsModal
				isOpen={showPermissionModal}
				permissionRequestTitle={t(
					'permissions:notification.passenger.request.title'
				)}
				permissionRequestDescription={t(
					'permissions:notification.passenger.request.description'
				)}
				permissionRequestButtonAcceptText={t(
					'permissions:notification.passenger.request.button_accept'
				)}
				permissionRequestButtonSkipText={t(
					'permissions:notification.passenger.request.button_skip'
				)}
				permissionDeniedTitle={t(
					'permissions:notification.passenger.denied.title'
				)}
				permissionDeniedDescription={t(
					'permissions:notification.passenger.denied.description'
				)}
				permissionDeniedButtonAcceptText={t(
					'permissions:notification.passenger.denied.button_accept'
				)}
				permissionDeniedButtonSkipText={t(
					'permissions:notification.passenger.denied.button_skip'
				)}
				animationElement={<StayInformedAnim />}
				checkPermissionsCallback={FirebaseMessaging.checkPermissions}
				askPermissionsCallback={FirebaseMessaging.requestPermissions}
				onPermissionsGranted={sendMessagingToken}
				onCancel={closePermissionModal}
			/>
		</ThemeProvider>
	);
};
